import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSports } from '../context/SportsProvider';
import GroupStage from './views/GroupStage/GroupStage';
import Ranking from './views/Ranking/Ranking';
import TournamentView from './views/TournamentView';
import { VIEWS, VIEW_LABELS } from './constants';
import { useStandingsScroll } from './hooks/useStandingsScroll';
import WCBackground from '../components/WCBackground';
import classNames from 'classnames';
import styles from './WCStandings.module.scss';
import { renderMaxLengthText } from 'utils/renderMaxLengthText';
import { getTranslationEmptyOrNullKey } from 'utils/getTranslation';
import { useSelector } from 'react-redux';
import { useMundialMetrics } from '../hooks/useMundialMetrics';
import { INTERACTION_TYPES } from '../hooks/useMundialMetrics/constants';
import { useMundialScrollTracker } from '../hooks/useMundialScrollTracker';

const SCREEN_PARAM = 'screen';
const VALID_VIEWS = Object.values(VIEWS);
const FALLBACK_VIEW = VIEWS.GROUP_STAGE;

const resolveDefaultView = currentStage =>
  currentStage === 'GROUP_STAGE' || currentStage === 'PHASE_GROUP'
    ? VIEWS.GROUP_STAGE
    : VIEWS.RANKING;

const WCStandings = () => {
  const translations = useSelector(state => state.translations);
  const [searchParams, setSearchParams] = useSearchParams();
  const { standings, currentStage: CS } = useSports();

  // Bandera: ¿ya resolvimos el default en ESTE ciclo de montaje?
  const [hasResolvedDefault, setHasResolvedDefault] = useState(false);

  const currentStage = CS?.stage?.code;
  const bootstrapError = standings?.error?.[VIEWS.GROUP_STAGE];

  useEffect(
    () => {
      if (hasResolvedDefault) return;

      const isReady = Boolean(bootstrapError) || Boolean(currentStage);

      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.delete('stage_code');
          next.delete('countryId');
          next.delete('matchId');

          if (isReady) {
            const resolved = bootstrapError
              ? FALLBACK_VIEW
              : resolveDefaultView(currentStage);
            next.set(SCREEN_PARAM, resolved);
          }

          return next;
        },
        { replace: true },
      );

      if (isReady) setHasResolvedDefault(true);
    },
    [hasResolvedDefault, currentStage, bootstrapError, setSearchParams],
  );

  // Después de resolver, la URL es source of truth para los cambios de subtab.
  const screenParam = searchParams.get(SCREEN_PARAM);
  const activeView = VALID_VIEWS.includes(screenParam)
    ? screenParam
    : FALLBACK_VIEW;

  const {
    subtabHidden,
    viewScrolled,
    viewAtBottom,
    standingsRef,
    viewRef,
    resetScroll,
  } = useStandingsScroll(activeView);

  const { trackSelectContent } = useMundialMetrics();

  const handleViewChange = useCallback(
    view => {
      const subTabLabel = getTranslationEmptyOrNullKey(VIEW_LABELS[view], translations);
      if (subTabLabel && !subTabLabel.startsWith('eventodeportivo_')) {
        trackSelectContent({
          pageTitleSegments: ['tabla de posiciones', subTabLabel],
          pageTitle: subTabLabel,
          contentSubsection: 'tabla de posiciones',
          moduloName: '',
          interactionType: INTERACTION_TYPES.MAIN_TABS,
        });
      }
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set(SCREEN_PARAM, view);
        return next;
      });
      resetScroll();
    },
    [setSearchParams, resetScroll, trackSelectContent, translations],
  );

  useMundialScrollTracker({
    pageTitleSegments: ['tabla de posiciones'],
    contentSubsection: 'tabla de posiciones',
    moduloName: '',
  });

  useEffect(() => {
    const tabpanel = document.querySelector('[data-role="mundial2026-tabpanel"]');
    if (tabpanel) tabpanel.setAttribute('data-standings-active', '');
    return () => {
      if (tabpanel) tabpanel.removeAttribute('data-standings-active');
    };
  }, []);

  const isResolvingDefault = !hasResolvedDefault;

  const standingsClass = classNames(styles['wc-standings'], {
    [styles['wc-standings--subtab-hidden']]: subtabHidden,
    [styles['wc-standings--scrolled']]: viewScrolled,
  });

  const viewClass = classNames(styles['wc-standings__view'], {
    [styles['wc-standings__view--at-bottom']]: viewAtBottom,
  });

  return (
    <div
      ref={standingsRef}
      className={standingsClass}
      data-subtab-hidden={subtabHidden ? '' : undefined}
      data-scrolled={viewScrolled ? '' : undefined}
    >
      <div className={styles['wc-standings__tabs']}>
        {VALID_VIEWS.map(view => (
          <button
            key={view}
            type="button"
            className={classNames(styles['wc-standings__tab'], {
              [styles['wc-standings__tab--active']]: activeView === view,
            })}
            onClick={() => handleViewChange(view)}
          >
            {renderMaxLengthText(
              getTranslationEmptyOrNullKey(VIEW_LABELS[view], translations),
              19,
            )}
          </button>
        ))}
      </div>
      <WCBackground noPaddingBottom={activeView === VIEWS.BRACKET}>
        <div ref={viewRef} className={viewClass} data-role="wc-standings-view">
          {!isResolvingDefault && activeView === VIEWS.GROUP_STAGE && (
            <GroupStage />
          )}
          {!isResolvingDefault && activeView === VIEWS.RANKING && <Ranking />}
          {!isResolvingDefault && activeView === VIEWS.BRACKET && (
            <TournamentView />
          )}
        </div>
      </WCBackground>
    </div>
  );
};

export default WCStandings;
